import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton, Box } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import MerchantContactForm from './MerchantContactForm';
import { getUrlParams } from '../../utils/getUrls';

const THEME = {
  navy: '#181436',
  orange: '#FF7A28',
  muted: '#6b7280',
  border: '#e8ebef',
  iconBg: '#FFF4ED',
  iconBorder: '#FFD9B8',
  pageBg: '#f4f5f6',
};

export default function ContactUsDialog({
  isMobile = false,
  text = 'Contact Us',
  wrapStyle = {},
  buttonStyle = {},
  backgroundColor = '#5145cd',
  textColor = '#ffffff',
  onSubmit, // async (values) => {}  — your API call
}) {
  const [open, setOpen] = useState(false);
  const [formStep, setFormStep] = useState('form');

  // Keep rep attribution from the URL, same as the calendar version did
  const { phone, repcode } = getUrlParams();
  const meta = {
    repcode: repcode || 'None',
    landingPhone: phone || 'None',
    submittedAt: new Date().toISOString(),
    contact_reason: 'new_account',
    reasonLabel: 'Open a new merchant account',
  };

  const isOtpStep = formStep === 'otp';
  // OTP stacks on mobile, so keep a compact card width
  const dialogWidth = isOtpStep ? 520 : 460;

  const handleOpen = useCallback(() => {
    setFormStep('form');
    setOpen(true);
  }, []);
  const handleClose = useCallback(() => {
    setOpen(false);
    setFormStep('form');
  }, []);

  return (
    <div style={{ ...wrapStyle }}>
      <button
        type='button'
        onClick={handleOpen}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          border: 'none',
          backgroundColor,
          color: textColor,
          borderRadius: '5px',
          width: '300px',
          padding: isMobile ? '12px 14px' : '12px 16px',
          marginTop: '20px',
          fontFamily: 'Inter Medium, Segoe UI, Arial, sans-serif',
          fontSize: isMobile ? 14 : 15,
          cursor: 'pointer',
          boxShadow: '0px 10px 24px rgba(0,0,0,0.18)',
          WebkitTapHighlightColor: 'transparent',
          ...buttonStyle,
        }}
      >
        <CalendarMonthIcon size={15} color='inherit' />
        {text}
      </button>

      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={false}
        fullWidth={false}
        maxWidth={false}
        scroll='paper'
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
          },
        }}
        PaperProps={{
          sx: {
            width: {
              xs: '100%',
              sm: Math.min(dialogWidth, 560),
              md: dialogWidth,
            },
            maxWidth: {
              xs: 'calc(100vw - 32px)',
              sm: Math.min(dialogWidth, 560),
              md: dialogWidth,
            },
            maxHeight: { xs: '90dvh', sm: '90vh' },
            borderRadius: '10px',
            m: 0,
            border: `1px solid ${THEME.border}`,
            boxShadow: '0 1px 4px rgba(24, 20, 54, 0.07)',
            backgroundColor: '#fff',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(24, 20, 54, 0.45)',
          },
        }}
      >
        <DialogTitle
          sx={{
            position: 'relative',
            px: { xs: 2, sm: 2.5 },
            pt: 2.5,
            pb: 0,
            flexShrink: 0,
            backgroundColor: THEME.pageBg,
            borderBottom: `1.5px solid #f0f1f3`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.75,
              pr: 5,
              pb: 2.25,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '10px',
                background: THEME.iconBg,
                border: `1px solid ${THEME.iconBorder}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <MailOutlineRoundedIcon sx={{ fontSize: 21, color: THEME.orange }} />
            </Box>
            <Box sx={{ pt: '3px', minWidth: 0 }}>
              <Box
                sx={{
                  fontSize: { xs: 15, sm: 16 },
                  fontWeight: 700,
                  color: THEME.navy,
                  mb: '3px',
                  lineHeight: 1.2,
                  fontFamily: 'Inter Medium, Segoe UI, Arial, sans-serif',
                }}
              >
                Tell us about your restaurant
              </Box>
              <Box
                sx={{
                  fontSize: 12.5,
                  color: THEME.muted,
                  lineHeight: 1.5,
                  fontFamily: 'Inter Regular, Segoe UI, Arial, sans-serif',
                }}
              >
                Share a few details and we&apos;ll reach out to schedule a visit.
              </Box>
            </Box>
          </Box>

          <IconButton
            onClick={handleClose}
            aria-label='Close'
            sx={{
              position: 'absolute',
              right: 10,
              top: 12,
              color: THEME.muted,
              '&:hover': {
                color: THEME.navy,
                backgroundColor: 'rgba(24, 20, 54, 0.06)',
              },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: { xs: 2, sm: isOtpStep ? 3 : 2.5 },
            pt: '20px !important',
            pb: 3,
            backgroundColor: '#fff',
            overflowY: 'auto',
            flex: '1 1 auto',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <MerchantContactForm
              onSubmit={onSubmit}
              meta={meta}
              onStepChange={setFormStep}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
}
